import { memo, useRef, useState } from 'react';
import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useContextBridge } from 'its-fine';
import NumberSpinner from '../../../NumberSpinner';
import waystationAnimations from './waystation.animation.json';
import noiseAnimation from './noise.animation.json';
import MicroFlicker from './MicroFlicker';
import { Vector3 } from 'three';
import {
  useSetRef,
} from '@artica/hooks';
import style, {
  pin as pinStyle,
  marker as markerStyle,
  numbers as numbersStyle,
  microFlicker as microFlickerStyle,

  noise as noiseStyle,
} from './index.style';
import {
  Visible,

  window,
  parallax,
} from '@immutabl3/alchemy';

const WaystationNumbers = ({ direction }) => {
  return (
    <div css={ numbersStyle } data-direction={ direction }>
      <div>
        <NumberSpinner value="2414646" />
      </div>
      <div>
        <NumberSpinner value="624646456444" /><i />
        <NumberSpinner value="555" /><i />
        <NumberSpinner value="2464" /><i />
        <NumberSpinner value="24" />
      </div>
      <div>
        <NumberSpinner value="731906962" /><i />
        <NumberSpinner value="542552" /><i />
        <NumberSpinner value="23" />
      </div>
    </div>
  );
};

const WaystationContent = memo(({ index }) => {
  return (
    <parallax.div css={ style } beaker="chapter1" animation={ waystationAnimations[index].container }>
      <div css={ pinStyle } />
      <parallax.div css={ markerStyle } beaker="chapter1" animation={ waystationAnimations[index].line } />
      <WaystationNumbers direction={ index === 1 ? 'left' : 'right' } />
      <parallax.div beaker="chapter1" animation={ waystationAnimations[index].sprites }>
        <MicroFlicker
          css={ microFlickerStyle }
          data-direction={ index === 1 ? 'left' : 'right' }
        />
      </parallax.div>
    </parallax.div>
  );
});

const Waystation = memo(function({ index }) {
  const Bridge = useContextBridge();
  return (
    <Html
      wrapperClass="no-interact"
      transform
      sprite
    >
      <Bridge>
        <WaystationContent index={ index } />
      </Bridge>
    </Html>
  );
});

// http://zachberry.com/blog/tracking-3d-objects-in-2d-with-three-js/
// and 
// https://github.com/mrdoob/three.js/issues/78
const nestedObjectToScreenXYZ = (vector, obj, camera) => {
  const width = window.width();
  const height = window.height();
  vector.setFromMatrixPosition(obj.matrixWorld);
  const widthHalf = (width / 2);
  const heightHalf = (height / 2);
  vector.project(camera);
  vector.x = (vector.x * widthHalf) + widthHalf;
  vector.y = -(vector.y * heightHalf) + heightHalf;
  return vector;
};

const Marker = memo(({ vector: v, position, children }) => {
  const group1 = useRef();
  const [vector] = useState(() => new Vector3());
  
  useFrame(({ camera }) => {
    const p = nestedObjectToScreenXYZ(vector, group1.current, camera);
    v.current.x = p.x;
    v.current.y = p.y;
  });

  return (
    <group
      ref={ group1 }
      position={ position }
    >
      { children }
    </group>
  );
});

export default function WaystationCapture({ index, position }) {
  const ref = useSetRef(`questionmark${index}`);
  if (!ref.current) ref.current = { x: 0, y: 0 };

  return (
    <Visible beaker="chapter1" animation={ waystationAnimations[index].timeline }>
      <Marker
        vector={ ref }
        position={ position }
      >
        <Waystation index={ index } />
      </Marker>
    </Visible>
  );
};

const NoiseContent = memo(({ index }) => {
  return (
    <parallax.div css={ [style, noiseStyle] } beaker="chapter1" animation={ noiseAnimation.container }>
      <parallax.div css={ markerStyle } beaker="chapter1" animation={ noiseAnimation.line } />
      <WaystationNumbers direction={ !index % 2 ? 'left' : 'right' } />
      <parallax.div beaker="chapter1" animation={ noiseAnimation.sprites }>
        <MicroFlicker
          css={ microFlickerStyle }
          data-direction={ !index % 2 ? 'left' : 'right' }
        />
      </parallax.div>
    </parallax.div>
  );
});

const Noise = memo(function({ index }) {
  const Bridge = useContextBridge();
  return (
    <Html
      wrapperClass="no-interact"
      transform
      sprite
    >
      <Bridge>
        <NoiseContent index={ index } />
      </Bridge>
    </Html>
  );
});

export const WaystationNoise = ({ index, position }) => {
  return (
    <Visible beaker="chapter1" animation={ noiseAnimation.timeline }>
      <group position={ position }>
        <Noise index={ index } />
      </group>
    </Visible>
  );
};